/**
 * muestraAleatoria -- seleccion diaria determinista de insumos para conteo por
 * muestreo. Mision: PANEL-OPERATIONS-001 / RAND-T1 (ASSIGN #5384).
 *
 * Problema que resuelve: un negocio con 800 referencias (piloto drogueria) no
 * cuenta todo el catalogo a diario. Cuando verificacion_config.tipo_conteo =
 * 'parcial_aleatorio', el kiosk propone una MUESTRA del dia de N insumos.
 *
 * Propiedades del muestreo:
 *  - DETERMINISTA POR DIA: la clave aleatoria de cada insumo se deriva de
 *    hash(businessId|fechaOperativa|nombreInsumo). Recargar la pagina, cambiar
 *    de dispositivo o reordenar el catalogo NO cambia la muestra del dia.
 *    Un insumo nuevo en el catalogo tampoco altera las claves de los demas
 *    (no hay PRNG secuencial compartido).
 *  - PONDERADA POR ANTIGUEDAD (cobertura rotativa): peso = dias desde el
 *    ultimo conteo (nunca contado = peso maximo). Muestreo sin reemplazo
 *    Efraimidis-Spirakis: key = u^(1/peso), se toman los N con key mas alta.
 *    Lo no contado hace mas tiempo tiene mas probabilidad, pero sigue siendo
 *    azar: el colaborador no puede predecir la lista (anti-gaming del conteo).
 *  - "PEDIR MAS": ordenCompleto expone el catalogo entero en el orden del dia;
 *    las tandas siguientes son los N siguientes de ese mismo orden.
 *
 * Modulo puro sin dependencias (patron shared/numeroColombiano.js): testeable
 * en consola y reutilizable por otras vistas (p.ej. auditoria del dueno).
 *
 * @module presentation/shared/muestraAleatoria
 */

/** Peso maximo (dias) para insumos nunca contados o sin historial. */
export const DIAS_PESO_MAX = 60;

/**
 * FNV-1a 32-bit sobre string -> uint32. Suficiente como semilla de PRNG
 * (no criptografico; aqui solo se necesita dispersion estable).
 * @param {string} str
 * @returns {number} uint32
 */
export function hashSemilla(str) {
  let h = 0x811c9dc5;
  const s = String(str);
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    // FNV prime 16777619 via shifts (evita overflow de float en multiplicacion directa)
    h = (h + ((h << 1) + (h << 4) + (h << 7) + (h << 8) + (h << 24))) >>> 0;
  }
  return h >>> 0;
}

/**
 * PRNG mulberry32: rapido, determinista, distribucion uniforme decente.
 * @param {number} seed uint32
 * @returns {() => number} generador de U(0,1)
 */
export function mulberry32(seed) {
  let a = seed >>> 0;
  return function () {
    a = (a + 0x6D2B79F5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/**
 * Fecha operativa en zona America/Bogota como 'YYYY-MM-DD'.
 * La semilla del dia usa ESTA fecha: a las 11pm y a las 6am del mismo dia
 * colombiano la muestra es la misma, sin importar el reloj UTC del dispositivo.
 * (Leccion timestamp Colombia de Asistencia V2: jamas derivar el dia operativo
 * de toISOString(), que es UTC y cambia de dia a las 7pm hora Colombia.)
 * @param {Date} [date]
 * @returns {string} YYYY-MM-DD
 */
export function fechaOperativaBogota(date = new Date()) {
  // en-CA formatea como YYYY-MM-DD directo.
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: 'America/Bogota', year: 'numeric', month: '2-digit', day: '2-digit'
  }).format(date);
}

/**
 * Dias sin contar por insumo a partir de las sesiones del historial de
 * verificaciones (InventarioApiClient.getHistorialBusiness shape).
 *
 * @param {Array<{fecha:string, items?:Array<{insumo?:string}>}>} sesiones
 * @param {string} [hoyISO] YYYY-MM-DD operativo (default: hoy Bogota)
 * @returns {Map<string, number>} nombreLowercase -> dias desde el ultimo conteo
 *   (0 = contado hoy). Insumos sin aparicion NO estan en el Map (el caller
 *   los trata como "nunca contados" = DIAS_PESO_MAX).
 */
export function antiguedadPorInsumo(sesiones, hoyISO) {
  const hoy = hoyISO || fechaOperativaBogota();
  const hoyMs = Date.parse(hoy + 'T00:00:00-05:00');
  const ultima = new Map(); // nombreLower -> YYYY-MM-DD mas reciente
  for (const s of (Array.isArray(sesiones) ? sesiones : [])) {
    // La fecha de sesion puede venir ISO completo; el dia operativo se toma en Bogota.
    const dia = s && s.fecha ? fechaOperativaBogota(new Date(s.fecha)) : null;
    if (!dia || isNaN(Date.parse(dia))) continue;
    for (const it of (Array.isArray(s.items) ? s.items : [])) {
      const nombre = it && it.insumo ? String(it.insumo).trim().toLowerCase() : null;
      if (!nombre) continue;
      const prev = ultima.get(nombre);
      if (!prev || dia > prev) ultima.set(nombre, dia);
    }
  }
  const out = new Map();
  for (const [nombre, dia] of ultima) {
    const diffDias = Math.floor((hoyMs - Date.parse(dia + 'T00:00:00-05:00')) / 86400000);
    out.set(nombre, Math.max(0, diffDias));
  }
  return out;
}

/**
 * Seleccion de la muestra del dia: ponderada, sin reemplazo, determinista.
 *
 * @param {object} params
 * @param {Array<{nombre:string}>} params.items catalogo elegible (ya filtrado por grupos del operador)
 * @param {number} params.tamano N insumos de la muestra (>=1)
 * @param {Map<string, number>} [params.antiguedad] nombreLower -> dias sin contar
 * @param {string} params.seedStr semilla del dia, p.ej. `${businessId}|${fechaOperativaBogota()}`
 * @returns {{ muestra: Array, ordenCompleto: Array, pesoDe: (nombre:string) => number }}
 *   ordenCompleto = catalogo entero en el orden del dia (muestra = primeros N);
 *   "pedir mas" toma las tandas siguientes de ese orden.
 */
export function seleccionarMuestraDiaria({ items, tamano, antiguedad, seedStr }) {
  const lista = Array.isArray(items) ? items.filter(i => i && i.nombre) : [];
  const n = Math.max(1, Math.floor(Number(tamano) || 1));
  const ant = antiguedad instanceof Map ? antiguedad : new Map();

  const pesoDe = (nombre) => {
    const dias = ant.get(String(nombre).trim().toLowerCase());
    // Nunca contado (sin historial) pesa el maximo; contado hoy pesa 1 (nunca 0:
    // todo insumo conserva probabilidad de salir -- azar auditable, no lista fija).
    if (dias === undefined) return DIAS_PESO_MAX;
    return Math.min(Math.max(dias, 1), DIAS_PESO_MAX);
  };

  const conKey = lista.map(item => {
    // u propio POR INSUMO derivado de la semilla del dia + nombre: estable ante
    // reordenamientos e inserciones en el catalogo (sin PRNG secuencial global).
    const u = mulberry32(hashSemilla(seedStr + '|' + String(item.nombre).trim().toLowerCase()))();
    const w = pesoDe(item.nombre);
    // Efraimidis-Spirakis: key = u^(1/w). En log-espacio para estabilidad numerica.
    const key = Math.log(Math.max(u, Number.MIN_VALUE)) / w;
    return { item, key };
  });

  conKey.sort((a, b) => b.key - a.key); // key mas alta primero
  const ordenCompleto = conKey.map(x => x.item);
  return { muestra: ordenCompleto.slice(0, n), ordenCompleto, pesoDe };
}

/**
 * Cobertura de conteo de los ultimos 30 dias: % del catalogo elegible con al
 * menos un conteo en ese periodo. Dato para el dueno (step 4 de RAND-T1).
 *
 * @param {Array<{nombre:string}>} items catalogo elegible
 * @param {Map<string, number>} antiguedad nombreLower -> dias sin contar
 * @returns {{ contados30d: number, total: number, pct: number }}
 */
export function cobertura30Dias(items, antiguedad) {
  const lista = Array.isArray(items) ? items.filter(i => i && i.nombre) : [];
  const ant = antiguedad instanceof Map ? antiguedad : new Map();
  let contados = 0;
  for (const it of lista) {
    const dias = ant.get(String(it.nombre).trim().toLowerCase());
    if (dias !== undefined && dias <= 30) contados++;
  }
  const total = lista.length;
  return { contados30d: contados, total, pct: total > 0 ? Math.round((contados / total) * 100) : 0 };
}
